import { toast } from 'sonner';

// Types for sync queue
interface SyncRequest {
  id: string;
  url: string;
  method: string;
  headers: Record<string, string>;
  body?: string;
  timestamp: number;
  retryCount: number;
  maxRetries: number;
  context?: string;
}

const SYNC_QUEUE_KEY = 'geniebot-sync-queue';
const MAX_RETRIES = 5;

// Check if Background Sync API is supported
export function isBackgroundSyncSupported(): boolean {
  return 'serviceWorker' in navigator && 'SyncManager' in window;
}

// IndexedDB helpers
function openSyncDB(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open('geniebot-sync', 1);
    
    request.onerror = () => reject(request.error);
    request.onsuccess = () => resolve(request.result);
    
    request.onupgradeneeded = (event) => {
      const db = (event.target as IDBOpenDBRequest).result;
      if (!db.objectStoreNames.contains('sync-queue')) {
        db.createObjectStore('sync-queue', { keyPath: 'id' });
      }
    };
  });
}

async function addToSyncDB(request: SyncRequest): Promise<void> {
  const db = await openSyncDB();
  return new Promise((resolve, reject) => {
    const transaction = db.transaction('sync-queue', 'readwrite');
    const store = transaction.objectStore('sync-queue');
    const req = store.put(request);
    
    req.onerror = () => reject(req.error);
    req.onsuccess = () => resolve();
  });
}

async function removeFromSyncDB(id: string): Promise<void> {
  const db = await openSyncDB();
  return new Promise((resolve, reject) => {
    const transaction = db.transaction('sync-queue', 'readwrite');
    const store = transaction.objectStore('sync-queue');
    const req = store.delete(id);
    
    req.onerror = () => reject(req.error);
    req.onsuccess = () => resolve();
  });
}

async function getAllFromSyncDB(): Promise<SyncRequest[]> {
  const db = await openSyncDB();
  return new Promise((resolve, reject) => {
    const transaction = db.transaction('sync-queue', 'readonly');
    const store = transaction.objectStore('sync-queue');
    const req = store.getAll();
    
    req.onerror = () => reject(req.error);
    req.onsuccess = () => resolve(req.result || []);
  });
}

async function clearSyncDB(): Promise<void> {
  const db = await openSyncDB();
  return new Promise((resolve, reject) => {
    const transaction = db.transaction('sync-queue', 'readwrite');
    const store = transaction.objectStore('sync-queue');
    const req = store.clear();
    
    req.onerror = () => reject(req.error);
    req.onsuccess = () => resolve();
  });
}

// Get the sync queue
export async function getSyncQueue(): Promise<SyncRequest[]> {
  try {
    return await getAllFromSyncDB();
  } catch {
    // Fallback to localStorage
    const stored = localStorage.getItem(SYNC_QUEUE_KEY);
    return stored ? JSON.parse(stored) : [];
  }
}

// Add a request to the sync queue
export async function queueForSync(
  url: string,
  options: RequestInit = {},
  context?: string
): Promise<string> {
  const id = `sync_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;
  
  const syncRequest: SyncRequest = {
    id,
    url,
    method: options.method || 'GET',
    headers: options.headers as Record<string, string> || {},
    body: options.body as string | undefined,
    timestamp: Date.now(),
    retryCount: 0,
    maxRetries: MAX_RETRIES,
    context,
  };

  // Store in IndexedDB
  try {
    await addToSyncDB(syncRequest);
  } catch {
    // Fallback to localStorage
    const queue = await getSyncQueue();
    queue.push(syncRequest);
    localStorage.setItem(SYNC_QUEUE_KEY, JSON.stringify(queue));
  }

  // Try to register for background sync
  if (isBackgroundSyncSupported()) {
    try {
      const registration = await navigator.serviceWorker.ready;
      // Use type assertion since SyncManager types aren't widely available
      if ('sync' in registration) {
        await (registration as any).sync.register('api-sync');
        console.log('[BackgroundSync] Registered sync event');
      }
    } catch (error) {
      console.warn('[BackgroundSync] Failed to register sync:', error);
    }
  }
  
  // Always set up the online listener as fallback
  setupOnlineListener();

  toast.info('Request queued for sync', {
    description: context || 'Will retry when connection is restored',
  });

  notifySyncQueueListeners();
  return id;
}

// Remove a request from the queue
export async function removeFromSyncQueue(id: string): Promise<void> {
  try {
    await removeFromSyncDB(id);
  } catch {
    const queue = await getSyncQueue();
    const filtered = queue.filter((r) => r.id !== id);
    localStorage.setItem(SYNC_QUEUE_KEY, JSON.stringify(filtered));
  }
  notifySyncQueueListeners();
}

// Clear the entire sync queue
export async function clearSyncQueue(): Promise<void> {
  try {
    await clearSyncDB();
  } catch {
    localStorage.removeItem(SYNC_QUEUE_KEY);
  }
  notifySyncQueueListeners();
}

// Process all queued requests
export async function processSyncQueue(): Promise<{
  successful: number;
  failed: number;
  remaining: number;
}> {
  const queue = await getSyncQueue();
  let successful = 0;
  let failed = 0;

  if (queue.length === 0) {
    return { successful: 0, failed: 0, remaining: 0 };
  }

  console.log(`[BackgroundSync] Processing ${queue.length} queued requests`);
  toast.info(`Syncing ${queue.length} pending request(s)...`);

  for (const request of queue) {
    if (request.retryCount >= request.maxRetries) {
      await removeFromSyncQueue(request.id);
      failed++;
      continue;
    }

    try {
      const response = await fetch(request.url, {
        method: request.method,
        headers: request.headers,
        body: request.body,
      });

      if (response.ok) {
        await removeFromSyncQueue(request.id);
        successful++;
        console.log(`[BackgroundSync] Successfully synced: ${request.id}`);
      } else if (response.status >= 500) {
        // Server error, retry later
        request.retryCount++;
        await addToSyncDB(request);
      } else {
        // Client error, remove from queue
        await removeFromSyncQueue(request.id);
        failed++;
      }
    } catch {
      // Network error, increment retry
      request.retryCount++;
      if (request.retryCount < request.maxRetries) {
        await addToSyncDB(request);
      } else {
        await removeFromSyncQueue(request.id);
        failed++;
      }
    }
  }

  const remaining = (await getSyncQueue()).length;

  if (successful > 0) {
    toast.success(`Synced ${successful} request(s) successfully`);
  }
  if (failed > 0) {
    toast.error(`${failed} request(s) failed after max retries`);
  }

  notifySyncQueueListeners();
  return { successful, failed, remaining };
}

// Setup fallback online listener
let onlineListenerSetup = false;

function setupOnlineListener(): void {
  if (onlineListenerSetup) return;
  onlineListenerSetup = true;

  window.addEventListener('online', async () => {
    const queue = await getSyncQueue();
    if (queue.length > 0) {
      // Small delay to ensure connection is stable
      setTimeout(() => {
        processSyncQueue();
      }, 1000);
    }
  });
}

// Initialize on module load
if (typeof window !== 'undefined') {
  setupOnlineListener();
  
  // Listen for messages from service worker
  navigator.serviceWorker?.addEventListener('message', (event) => {
    if (event.data?.type === 'SYNC_SUCCESS') {
      removeFromSyncQueue(event.data.id);
    }
  });
}

// Create a fetch wrapper that queues on failure
export async function fetchWithSync(
  url: string,
  options: RequestInit = {},
  context?: string
): Promise<Response> {
  try {
    const response = await fetch(url, options);
    return response;
  } catch (error) {
    // Network error - queue for background sync
    if (!navigator.onLine) {
      await queueForSync(url, options, context);
      throw new Error('Request queued for background sync');
    }
    throw error;
  }
}

// Get pending sync count
export async function getPendingSyncCount(): Promise<number> {
  const queue = await getSyncQueue();
  return queue.length;
}

// Listen for sync queue changes
type SyncQueueListener = (count: number) => void;
const syncQueueListeners = new Set<SyncQueueListener>();

export function subscribeSyncQueue(listener: SyncQueueListener): () => void {
  syncQueueListeners.add(listener);
  // Notify immediately with current count
  getPendingSyncCount().then(listener);
  return () => syncQueueListeners.delete(listener);
}

// Notify listeners when queue changes
async function notifySyncQueueListeners(): Promise<void> {
  const count = await getPendingSyncCount();
  syncQueueListeners.forEach((listener) => listener(count));
}
